import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './FantasyPreview.css';

interface TeamRankings {
  teamId: number;
  teamName: string;
  ownerName: string;
  projectedRanking: number;
  projectedFloor: number;
  projectedCeiling: number;
  qbRank: number;
  rbRank: number;
  wrRank: number;
  teRank: number;
  longTermValueRank: number;
  featuredPlayerImage?: string;
  commentary: string;
  teamLogo?: string;
}

const MOCK_TEAM_DATA: TeamRankings[] = [
  {
    teamId: 1,
    teamName: "Championship Contenders",
    ownerName: "Owner 1",
    projectedRanking: 1,
    projectedFloor: 3,
    projectedCeiling: 1,
    qbRank: 2,
    rbRank: 1,
    wrRank: 3,
    teRank: 4,
    longTermValueRank: 2,
    commentary: "This team is loaded with talent across all positions. Strong depth at RB and elite QB play should carry them through the season. The addition of a top-tier WR in the draft gives them the firepower needed for a championship run. Their bench depth is particularly impressive, giving them the flexibility to weather injuries and make strategic trades throughout the season.",
    featuredPlayerImage: "https://via.placeholder.com/300x400/1f4e79/ffffff?text=Star+Player"
  },
  {
    teamId: 2,
    teamName: "Dynasty Builders",
    ownerName: "Owner 2",
    projectedRanking: 2,
    projectedFloor: 4,
    projectedCeiling: 1,
    qbRank: 1,
    rbRank: 5,
    wrRank: 1,
    teRank: 8,
    longTermValueRank: 1,
    commentary: "Built for the future with young superstars at every key position. While they may struggle with depth this year, their core is championship-caliber for years to come. The QB position is set for the next decade, and their wide receiver room is the envy of the league. Expect them to be aggressive in trades to shore up their running back situation.",
    featuredPlayerImage: "https://via.placeholder.com/300x400/2d5a27/ffffff?text=Dynasty+Star"
  },
  {
    teamId: 3,
    teamName: "Veteran Squad",
    ownerName: "Owner 3",
    projectedRanking: 3,
    projectedFloor: 1,
    projectedCeiling: 6,
    qbRank: 3,
    rbRank: 2,
    wrRank: 4,
    teRank: 1,
    longTermValueRank: 7,
    commentary: "Experience and consistency define this roster. They may not have the highest ceiling, but their veteran presence and reliable floor make them a playoff lock. Their tight end position is elite, and they have the deepest running back room in the league. Age concerns at wide receiver could be their achilles heel.",
    featuredPlayerImage: "https://via.placeholder.com/300x400/8b4513/ffffff?text=Veteran+Leader"
  },
  {
    teamId: 4,
    teamName: "Sleeper Picks",
    ownerName: "Owner 4",
    projectedRanking: 4,
    projectedFloor: 2,
    projectedCeiling: 8,
    qbRank: 6,
    rbRank: 3,
    wrRank: 5,
    teRank: 3,
    longTermValueRank: 4,
    commentary: "This team is full of high-upside players who could breakout. If their young talent develops as expected, they could surprise everyone and make a deep playoff run. Their draft was particularly impressive, landing several players with significant upside potential. The key will be whether their risky picks pay off.",
    featuredPlayerImage: "https://via.placeholder.com/300x400/4a148c/ffffff?text=Breakout+Star"
  },
  {
    teamId: 5,
    teamName: "Solid Foundation",
    ownerName: "Owner 5",
    projectedRanking: 5,
    projectedFloor: 5,
    projectedCeiling: 3,
    qbRank: 4,
    rbRank: 6,
    wrRank: 2,
    teRank: 7,
    longTermValueRank: 5,
    commentary: "A well-balanced team with no glaring weaknesses. Their elite WR corps could carry them to the playoffs if everything falls into place. The quarterback situation is stable, and they have good depth across most positions. Their success will depend on staying healthy and getting consistent production from their skill positions.",
    featuredPlayerImage: "https://via.placeholder.com/300x400/d32f2f/ffffff?text=Elite+WR"
  },
  {
    teamId: 6,
    teamName: "High Risk High Reward",
    ownerName: "Owner 6",
    projectedRanking: 6,
    projectedFloor: 9,
    projectedCeiling: 2,
    qbRank: 5,
    rbRank: 4,
    wrRank: 8,
    teRank: 2,
    longTermValueRank: 8,
    commentary: "Boom or bust perfectly describes this roster. They have the talent to compete with anyone on any given week, but consistency issues could derail their season. Their tight end is among the league's best, but question marks at wide receiver make them unpredictable. This team will either make a championship run or miss the playoffs entirely.",
    featuredPlayerImage: "https://via.placeholder.com/300x400/ff5722/ffffff?text=Boom+Bust"
  },
  {
    teamId: 7,
    teamName: "Rebuilding Year",
    ownerName: "Owner 7",
    projectedRanking: 7,
    projectedFloor: 6,
    projectedCeiling: 9,
    qbRank: 8,
    rbRank: 7,
    wrRank: 6,
    teRank: 5,
    longTermValueRank: 3,
    commentary: "This team is clearly in rebuild mode, but they've made smart moves for the future. Don't expect much this year, but they'll be dangerous in 2-3 seasons. They've accumulated significant draft capital and young talent that should pay dividends down the road. Patience will be key for this franchise.",
    featuredPlayerImage: "https://via.placeholder.com/300x400/607d8b/ffffff?text=Future+Star"
  },
  {
    teamId: 8,
    teamName: "Question Marks",
    ownerName: "Owner 8",
    projectedRanking: 8,
    projectedFloor: 7,
    projectedCeiling: 5,
    qbRank: 7,
    rbRank: 9,
    wrRank: 7,
    teRank: 6,
    longTermValueRank: 9,
    commentary: "Too many uncertainties surround this roster. Health concerns and age-related decline could make this a challenging season for what was once a powerhouse team. Several key players are coming off injury-shortened seasons, and their depth is concerning. They'll need everything to go right to compete.",
    featuredPlayerImage: "https://via.placeholder.com/300x400/795548/ffffff?text=Question+Mark"
  },
  {
    teamId: 9,
    teamName: "Dark Horse",
    ownerName: "Owner 9",
    projectedRanking: 9,
    projectedFloor: 8,
    projectedCeiling: 4,
    qbRank: 9,
    rbRank: 8,
    wrRank: 9,
    teRank: 9,
    longTermValueRank: 6,
    commentary: "Don't sleep on this team. While they may not look impressive on paper, they have a knack for finding value and could surprise people if their sleepers hit. Their owner has a history of identifying breakout players before anyone else. This could be the year their strategy finally pays off in a big way.",
    featuredPlayerImage: "https://via.placeholder.com/300x400/388e3c/ffffff?text=Dark+Horse"
  },
  {
    teamId: 10,
    teamName: "Bottom Feeders",
    ownerName: "Owner 10",
    projectedRanking: 10,
    projectedFloor: 10,
    projectedCeiling: 7,
    qbRank: 10,
    rbRank: 10,
    wrRank: 10,
    teRank: 10,
    longTermValueRank: 10,
    commentary: "A tough season ahead for this squad. Limited talent and poor draft capital make it difficult to see a path to success. Focus should be on development and future planning. However, in fantasy football, anything can happen, and they'll be looking to play spoiler while building for the future. Every team needs to start somewhere.",
    featuredPlayerImage: "https://via.placeholder.com/300x400/9e9e9e/ffffff?text=Rebuilding"
  }
];

export const TeamPreview: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const [teamData, setTeamData] = useState<TeamRankings | null>(null);
  const [allTeams, setAllTeams] = useState<TeamRankings[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading data - replace with actual API call
    setTimeout(() => {
      const team = MOCK_TEAM_DATA.find(t => t.teamId === parseInt(teamId || '0'));
      setTeamData(team || null);
      setAllTeams(MOCK_TEAM_DATA);
      setLoading(false);
    }, 500);
  }, [teamId]);

  if (loading) {
    return (
      <div className="fantasy-preview-loading">
        <h2>Loading Team Preview...</h2>
      </div>
    );
  }

  if (!teamData) {
    return (
      <div className="fantasy-preview-error">
        <h2>Team Not Found</h2>
        <Link to="/fantasy-preview">← Back to Rankings</Link>
      </div>
    );
  }

  const getRankingClass = (rank: number) => {
    if (rank <= 3) return 'rank-excellent';
    if (rank <= 6) return 'rank-good';
    if (rank <= 8) return 'rank-average';
    return 'rank-poor';
  };

  return (
    <div className="fantasy-preview-container">
      <header className="magazine-header">
        <h1 className="magazine-title">Fantasy Football Preview 2025</h1>
        <div className="magazine-date">Team Analysis | July 2025</div>
      </header>

      <nav className="preview-navigation">
        <Link to="/fantasy-preview" className="nav-link">
          Overall Rankings
        </Link>
        {allTeams.map(team => (
          <Link 
            key={team.teamId} 
            to={`/fantasy-preview/team/${team.teamId}`} 
            className={`nav-link ${team.teamId === teamData.teamId ? 'active' : ''}`}
          >
            {team.teamName}
          </Link>
        ))}
      </nav>

      <main className="team-preview">
        <div className="team-header">
          <div className="team-title-section">
            <h1 className="team-title">{teamData.teamName}</h1>
            <h2 className="owner-name">Managed by {teamData.ownerName}</h2>
            <div className="overall-ranking">
              <span className="rank-label">2025 Projected Ranking</span>
              <span className={`rank-number ${getRankingClass(teamData.projectedRanking)}`}>
                #{teamData.projectedRanking}
              </span>
            </div>
          </div>
          
          {teamData.featuredPlayerImage && (
            <div className="featured-player">
              <img 
                src={teamData.featuredPlayerImage} 
                alt="Featured Player" 
                className="player-image"
              />
              <div className="player-caption">Featured Player</div>
            </div>
          )}
        </div>

        <div className="team-analysis-grid">
          <section className="rankings-breakdown">
            <h3>Team Rankings Breakdown</h3>
            <div className="rankings-stats">
              <div className="ranking-stat">
                <span className="stat-label">Projected Floor</span>
                <span className={`stat-value ${getRankingClass(teamData.projectedFloor)}`}>
                  #{teamData.projectedFloor}
                </span>
              </div>
              <div className="ranking-stat">
                <span className="stat-label">Projected Ceiling</span>
                <span className={`stat-value ${getRankingClass(teamData.projectedCeiling)}`}>
                  #{teamData.projectedCeiling}
                </span>
              </div>
              <div className="ranking-stat">
                <span className="stat-label">Long-term Value</span>
                <span className={`stat-value ${getRankingClass(teamData.longTermValueRank)}`}>
                  #{teamData.longTermValueRank}
                </span>
              </div>
            </div>
          </section>

          <section className="position-rankings">
            <h3>Position Group Rankings</h3>
            <div className="position-grid">
              <div className="position-rank-card">
                <div className="position-label qb-bg">QB</div>
                <div className={`position-rank ${getRankingClass(teamData.qbRank)}`}>
                  #{teamData.qbRank}
                </div>
                <div className="position-description">Quarterback</div>
              </div>
              <div className="position-rank-card">
                <div className="position-label rb-bg">RB</div>
                <div className={`position-rank ${getRankingClass(teamData.rbRank)}`}>
                  #{teamData.rbRank}
                </div>
                <div className="position-description">Running Back</div>
              </div>
              <div className="position-rank-card">
                <div className="position-label wr-bg">WR</div>
                <div className={`position-rank ${getRankingClass(teamData.wrRank)}`}>
                  #{teamData.wrRank}
                </div>
                <div className="position-description">Wide Receiver</div>
              </div>
              <div className="position-rank-card">
                <div className="position-label te-bg">TE</div>
                <div className={`position-rank ${getRankingClass(teamData.teRank)}`}>
                  #{teamData.teRank}
                </div>
                <div className="position-description">Tight End</div>
              </div>
            </div>
          </section>

          <section className="team-commentary">
            <h3>Expert Analysis</h3>
            <div className="commentary-content">
              <p>{teamData.commentary}</p>
            </div>
          </section>

          <section className="team-outlook">
            <h3>Season Outlook</h3>
            <div className="outlook-bars">
              <div className="outlook-item">
                <span className="outlook-label">Championship Odds</span>
                <div className="outlook-bar">
                  <div 
                    className="outlook-fill championship" 
                    style={{ width: `${Math.max(5, (11 - teamData.projectedRanking) * 10)}%` }}
                  ></div>
                </div>
                <span className="outlook-percentage">
                  {Math.max(5, (11 - teamData.projectedRanking) * 10)}%
                </span>
              </div>
              <div className="outlook-item">
                <span className="outlook-label">Playoff Chances</span>
                <div className="outlook-bar">
                  <div 
                    className="outlook-fill playoff" 
                    style={{ width: `${Math.max(15, (11 - teamData.projectedRanking) * 15)}%` }}
                  ></div>
                </div>
                <span className="outlook-percentage">
                  {Math.max(15, (11 - teamData.projectedRanking) * 15)}%
                </span>
              </div>
              <div className="outlook-item">
                <span className="outlook-label">Breakout Potential</span>
                <div className="outlook-bar">
                  <div 
                    className="outlook-fill breakout" 
                    style={{ width: `${teamData.projectedRanking > teamData.projectedCeiling ? (teamData.projectedRanking - teamData.projectedCeiling) * 20 : 25}%` }}
                  ></div>
                </div>
                <span className="outlook-percentage">
                  {teamData.projectedRanking > teamData.projectedCeiling ? (teamData.projectedRanking - teamData.projectedCeiling) * 20 : 25}%
                </span>
              </div>
            </div>
          </section>
        </div>

        <nav className="team-navigation">
          <div className="nav-buttons">
            {teamData.teamId > 1 && (
              <Link 
                to={`/fantasy-preview/team/${teamData.teamId - 1}`} 
                className="nav-btn prev-btn"
              >
                ← Previous Team
              </Link>
            )}
            <Link to="/fantasy-preview" className="nav-btn home-btn">
              Back to Rankings
            </Link>
            {teamData.teamId < 10 && (
              <Link 
                to={`/fantasy-preview/team/${teamData.teamId + 1}`} 
                className="nav-btn next-btn"
              >
                Next Team →
              </Link>
            )}
          </div>
        </nav>
      </main>
    </div>
  );
};

export default TeamPreview;
